import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import Snackbar from './Snackbar';
import './ProductRateManagement.css';

const ProductRateManagement = ({ user, onShowNotification }) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [formData, setFormData] = useState({
    cover_type: 'TPO',
    vehicle_class: '',
    period: '',
    base_amount: '',
    is_tonnage_based: false,
    is_psv_pricing: false,
    passenger_fee: 500,
    tonnage_ranges: null,
    is_net: false
  });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('success');
  const [filterCoverType, setFilterCoverType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  useEffect(() => {
    fetchRates();
  }, [currentPage, filterCoverType]);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl('/api/admin/product-rates'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch product rates');
      }

      const data = await response.json();
      let filteredRates = data.rates || [];
      
      if (filterCoverType !== 'all') {
        filteredRates = filteredRates.filter(rate => rate.cover_type === filterCoverType);
      }
      
      // Pagination: 5 items per page
      const itemsPerPage = 5;
      const total = filteredRates.length;
      const totalPages = Math.ceil(total / itemsPerPage);
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedRates = filteredRates.slice(startIndex, endIndex);
      
      setRates(paginatedRates);
      setPagination({
        total,
        totalPages,
        itemsPerPage
      });
    } catch (error) {
      console.error('Error fetching rates:', error);
      showSnackbarMessage('Failed to fetch product rates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbarMessage = (message, type = 'success') => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setShowSnackbar(true);
    if (onShowNotification) {
      onShowNotification(message, type);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('adminToken');
      const body = { ...formData };
      if (editingRate?.id) body.id = editingRate.id;
      const response = await fetch(getApiUrl('/api/admin/product-rates'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save product rate');
      }

      showSnackbarMessage(editingRate ? 'Product rate updated successfully' : 'Product rate created successfully', 'success');
      setShowForm(false);
      setEditingRate(null);
      setFormData({
        cover_type: 'TPO',
        vehicle_class: '',
        period: '',
        base_amount: '',
        is_tonnage_based: false,
        is_psv_pricing: false,
        passenger_fee: 500,
        tonnage_ranges: null,
        is_net: false
      });
      fetchRates();
    } catch (error) {
      console.error('Error saving rate:', error);
      showSnackbarMessage(error.message || 'Failed to save product rate', 'error');
    }
  };

  const handleEdit = (rate) => {
    setEditingRate(rate);
    setFormData({
      cover_type: rate.cover_type,
      vehicle_class: rate.vehicle_class,
      period: rate.period,
      base_amount: rate.base_amount,
      is_tonnage_based: rate.is_tonnage_based || false,
      is_psv_pricing: rate.is_psv_pricing || false,
      passenger_fee: rate.passenger_fee || 500,
      tonnage_ranges: rate.tonnage_ranges,
      is_net: rate.is_net || false
    });
    setShowForm(true);
  };

  const handleToggleStatus = async (rateId, currentStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/admin/product-rates/${rateId}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update rate status');
      }

      showSnackbarMessage(`Product rate ${!currentStatus ? 'activated' : 'deactivated'} successfully`, 'success');
      fetchRates();
    } catch (error) {
      console.error('Error updating rate status:', error);
      showSnackbarMessage(error.message || 'Failed to update rate status', 'error');
    }
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm('Are you sure you want to delete this product rate?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(getApiUrl(`/api/admin/product-rates/${rateId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete product rate');
      }

      showSnackbarMessage('Product rate deleted successfully', 'success');
      fetchRates();
    } catch (error) {
      console.error('Error deleting rate:', error);
      showSnackbarMessage(error.message || 'Failed to delete product rate', 'error');
    }
  };

  const formatCurrency = (amount) => {
    return `KES ${parseFloat(amount).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="product-rate-management">
      <div className="management-header">
        <div>
          <h2>Product Rate Management</h2>
          <p>Manage pricing rates for TPO and TOR covers</p>
        </div>
      </div>

      {/* Top Action Bar */}
      <div className="top-action-bar">
        <div className="filter-section">
          <label htmlFor="cover-type-filter" className="filter-label">Filter by Cover Type:</label>
          <select
            id="cover-type-filter"
            className="cover-type-filter"
            value={filterCoverType}
            onChange={(e) => {
              setFilterCoverType(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">All Cover Types</option>
            <option value="TPO">TPO</option>
            <option value="TOR">TOR</option>
          </select>
        </div>
        <button className="btn-create" onClick={() => {
        setShowForm(true);
        setEditingRate(null);
        setFormData({
          cover_type: 'TPO',
          vehicle_class: '',
          period: '',
          base_amount: '',
          is_tonnage_based: false,
          is_psv_pricing: false,
          passenger_fee: 500,
          tonnage_ranges: null,
          is_net: false
        });
      }}>
          <span className="btn-icon">+</span>
          <span>Add Rate</span>
        </button>
      </div>

      {showForm && (
        <div className="rate-form-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingRate ? 'Edit Amount' : 'Add New Product Rate'}</h3>
              <button className="close-modal" onClick={() => {
                setShowForm(false);
                setEditingRate(null);
              }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {editingRate ? (
                <>
                  <div className="form-group" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f1f5f9', borderRadius: 6 }}>
                    <div style={{ fontSize: '0.9rem', color: '#475569' }}>
                      <strong>{editingRate.cover_type}</strong> · {editingRate.vehicle_class} · {editingRate.period}
                    </div>
                    <small style={{ color: '#64748b' }}>Product cannot be changed; only the amount is editable.</small>
                  </div>
                  <div className="form-group">
                    <label>{formData.cover_type === 'TOR' ? 'Premium (gross, customer pays) KES' : 'Basic (net) Premium KES'} *</label>
                    <input
                      type="number"
                      name="base_amount"
                      value={formData.base_amount}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  {formData.is_psv_pricing && (
                    <div className="form-group">
                      <label>Passenger Fee (KES per passenger)</label>
                      <input
                        type="number"
                        name="passenger_fee"
                        value={formData.passenger_fee}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                      />
                    </div>
                  )}
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="is_net"
                        checked={formData.is_net}
                        onChange={handleInputChange}
                      />
                      Net rate (no agent commission)
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Cover Type *</label>
                      <select
                        name="cover_type"
                        value={formData.cover_type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="TPO">TPO</option>
                        <option value="TOR">TOR</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Vehicle Class *</label>
                      <input
                        type="text"
                        name="vehicle_class"
                        value={formData.vehicle_class}
                        onChange={handleInputChange}
                        placeholder="e.g., Motor Private"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Period *</label>
                      <select
                        name="period"
                        value={formData.period}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Period</option>
                        <option value="1 Year">1 Year</option>
                        <option value="1 Month">1 Month</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{formData.cover_type === 'TOR' ? 'Premium (gross, customer pays) KES' : 'Basic (net) Premium KES'} *</label>
                      <input
                        type="number"
                        name="base_amount"
                        value={formData.base_amount}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          name="is_tonnage_based"
                          checked={formData.is_tonnage_based}
                          onChange={handleInputChange}
                        />
                        Tonnage-Based Pricing
                      </label>
                    </div>
                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          name="is_psv_pricing"
                          checked={formData.is_psv_pricing}
                          onChange={handleInputChange}
                        />
                        PSV Pricing (Passenger-Based)
                      </label>
                    </div>
                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          name="is_net"
                          checked={formData.is_net}
                          onChange={handleInputChange}
                        />
                        Net rate (no agent commission)
                      </label>
                    </div>
                  </div>
                  {formData.is_psv_pricing && (
                    <div className="form-group">
                      <label>Passenger Fee (KES per passenger)</label>
                      <input
                        type="number"
                        name="passenger_fee"
                        value={formData.passenger_fee}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                      />
                    </div>
                  )}
                </>
              )}
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => {
                  setShowForm(false);
                  setEditingRate(null);
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingRate ? 'Update Amount' : 'Create Rate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading product rates...</p>
        </div>
      ) : rates.length === 0 ? (
        <div className="empty-state">
          <p>No product rates found. Click "Add Rate" to create one.</p>
        </div>
      ) : (
        <>
          <p className="rates-info-note" style={{ marginBottom: '0.75rem', fontSize: '0.9rem', color: '#64748b' }}>
            <strong>Net</strong> = premium before levies; <strong>Gross</strong> = what the customer pays (net + levies). TPO stores net in DB; TOR stores gross; both display net &lt; gross here.
          </p>
          <div className="rates-table-container">
            <table className="rates-table">
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>Cover Type</th>
                  <th style={{ width: '220px' }}>Vehicle Class</th>
                  <th style={{ width: '100px' }}>Period</th>
                  <th style={{ width: '130px' }}>Net (basic)</th>
                  <th style={{ width: '130px' }}>Gross (customer pays)</th>
                  <th style={{ width: '80px' }}>No commission</th>
                  <th style={{ width: '130px' }}>Last Updated</th>
                  <th style={{ width: '320px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((rate) => (
                  <tr key={rate.id}>
                    <td><span className="badge badge-tpo">{rate.cover_type}</span></td>
                    <td>{rate.vehicle_class}</td>
                    <td>{rate.period}</td>
                    <td className="amount-cell">{formatCurrency(rate.net_amount ?? rate.base_amount)}</td>
                    <td className="amount-cell">{formatCurrency(rate.gross_amount ?? (rate.cover_type === 'TOR' ? rate.base_amount : parseFloat((rate.base_amount * 1.0045 + 40).toFixed(2))))}</td>
                    <td>{rate.is_net ? 'Yes' : 'No'}</td>
                    <td>{formatDate(rate.updated_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(rate)}
                          className="btn-edit"
                          title="Edit rate"
                        >
                          <span className="btn-icon">✏️</span>
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleToggleStatus(rate.id, rate.is_active)}
                          className={`toggle-status-btn ${rate.is_active ? 'deactivate' : 'activate'}`}
                          title={rate.is_active ? 'Deactivate rate' : 'Activate rate'}
                        >
                          <span className="btn-icon">{rate.is_active ? '🚫' : '✅'}</span>
                          <span>{rate.is_active ? 'Deactivate' : 'Activate'}</span>
                        </button>
                        <button
                          onClick={() => handleDelete(rate.id)}
                          className="btn-delete"
                          title="Delete rate"
                        >
                          <span className="btn-icon">🗑️</span>
                          <span>Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {pagination.totalPages} ({pagination.total} total rates)
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                disabled={currentPage === pagination.totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
          duration={5000}
        />
      )}
    </div>
  );
};

export default ProductRateManagement;

